import moment from "moment";
import { getStudentLocation } from "./getStudentLocation";

export const STUDENT_CSV_FIELDS = [
  { key: "name", label: "FirstName", defaultSelected: true },
  { key: "surname", label: "LastName", defaultSelected: true },
  { key: "email", label: "Email", defaultSelected: true },
  { key: "phone", label: "Phone", defaultSelected: false },
  { key: "dni", label: "DNI", defaultSelected: false },
  { key: "classroom", label: "Classroom", defaultSelected: false },
  { key: "status", label: "Status", defaultSelected: false },
  { key: "createdAt", label: "CreatedAt", defaultSelected: false },
  { key: "country", label: "Country", defaultSelected: false },
  { key: "region", label: "Region", defaultSelected: false },
];

export const DEFAULT_CSV_FIELDS = STUDENT_CSV_FIELDS.filter(
  (field) => field.defaultSelected
).map((field) => field.key);

/**
 * Maps a student record to its plain-text CSV cell value.
 * @param {object} student student document from the API
 * @param {string} fieldKey one of STUDENT_CSV_FIELDS keys
 * @returns {string} display value, with classroom joined, status reduced to latest, location resolved
 */
export function getStudentCsvValue(student, fieldKey) {
  const safeStudent = student || {};
  switch (fieldKey) {
    case "name":
      return safeStudent.name || "";
    case "surname":
      return safeStudent.surname || "";
    case "email":
      return safeStudent.email || "";
    case "phone":
      return safeStudent.phone || "";
    case "dni":
      return safeStudent.dni || "";
    case "classroom":
      if (Array.isArray(safeStudent.classroom)) {
        return safeStudent.classroom.filter(Boolean).join("; ");
      }
      return safeStudent.classroom || "";
    case "status":
      if (Array.isArray(safeStudent.isActive) && safeStudent.isActive.length > 0) {
        return safeStudent.isActive[safeStudent.isActive.length - 1];
      }
      return Array.isArray(safeStudent.isActive) ? "" : safeStudent.isActive || "";
    case "createdAt":
      return safeStudent.createdAt
        ? moment(safeStudent.createdAt).format("DD/MM/YYYY")
        : "";
    case "country": {
      const location = getStudentLocation(safeStudent);
      return (location.country || "").split(" *")[0];
    }
    case "region": {
      const location = getStudentLocation(safeStudent);
      return location.region || "";
    }
    default:
      return "";
  }
}

function escapeCsvCell(value) {
  const text = value === null || value === undefined ? "" : String(value);
  if (text.includes('"') || text.includes(",") || text.includes("\n") || text.includes("\r")) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return `"${text}"`;
}

/**
 * Serializes students to RFC4180 CSV with BOM for Excel.
 * @param {Array} students student documents to serialize
 * @param {Array<string>} selectedFields field keys in export order
 * @param {boolean} includeHeaders whether to prepend the label row
 * @returns {string} CSV text with BOM
 */
export function buildStudentsCsv(students, selectedFields, includeHeaders) {
  const rows = Array.isArray(students) ? students : [];
  const fields = STUDENT_CSV_FIELDS.filter((field) =>
    selectedFields.includes(field.key)
  );
  const lines = [];
  if (includeHeaders) {
    lines.push(fields.map((field) => escapeCsvCell(field.label)).join(","));
  }
  rows.forEach((student) => {
    lines.push(
      fields
        .map((field) => escapeCsvCell(getStudentCsvValue(student, field.key)))
        .join(",")
    );
  });
  return `\uFEFF${lines.join("\r\n")}`;
}

export function downloadCsvFile(fileName, csvText) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function buildExportFileName() {
  return `estudiantes_${moment().format("YYYYMMDD_HHmm")}.csv`;
}
